import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobile } from "@fortawesome/sharp-regular-svg-icons";
import { usePostHog } from "posthog-js/react";
import {
  AppealSupportingDocumentType,
  GenerateTemporaryUploadResult,
} from "../types";

import styles from "./qr-code-upload-section.module.scss";

type PollingState = "idle" | "waiting";

export type QrCodeUploadSectionVariant = "default" | "compact";

type QrCodeUploadSectionProps = {
  appealId: string;
  documentType: AppealSupportingDocumentType;
  onFilesUploaded: (files: File[]) => void;
  disabled: boolean;
  phoneUploadPath: string;
  generateTemporaryUpload: (
    appealId: string,
    documentType: AppealSupportingDocumentType,
  ) => Promise<GenerateTemporaryUploadResult>;
  placeholderFileName?: string;
  phoneUploadParams?: Record<string, string>;
  variant?: QrCodeUploadSectionVariant;
};

const POLLING_INTERVAL_MS = 1000;
const DEFAULT_PLACEHOLDER_FILE_NAME = "phone-upload.pdf";
const DEFAULT_FILE_TYPE = "application/pdf";

export const QrCodeUploadSection = ({
  appealId,
  documentType,
  onFilesUploaded,
  disabled,
  phoneUploadPath,
  generateTemporaryUpload,
  placeholderFileName = DEFAULT_PLACEHOLDER_FILE_NAME,
  phoneUploadParams,
  variant = "default",
}: QrCodeUploadSectionProps) => {
  const posthog = usePostHog();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [qrValue, setQrValue] = useState<string | null>(null);
  const [pollingState, setPollingState] = useState<PollingState>("idle");
  const [initError, setInitError] = useState<string | null>(null);
  const [sessionKey, setSessionKey] = useState(0);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const isMountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const initializeQrCode = useCallback(async () => {
    setQrValue(null);
    setDownloadUrl(null);
    setInitError(null);
    setPollingState("idle");

    try {
      const result = await generateTemporaryUpload(appealId, documentType);

      if (!isMountedRef.current) {
        return;
      }

      const target = new URL(phoneUploadPath, window.location.origin);
      target.searchParams.set("token", result.uploadToken);
      if (phoneUploadParams) {
        for (const [paramKey, paramValue] of Object.entries(
          phoneUploadParams,
        )) {
          target.searchParams.set(paramKey, paramValue);
        }
      }

      setQrValue(target.toString());
      setDownloadUrl(result.downloadUrl);
      setPollingState("waiting");
    } catch {
      if (isMountedRef.current) {
        setInitError("Failed to initialize upload session.");
      }
    }
  }, [
    appealId,
    documentType,
    generateTemporaryUpload,
    phoneUploadPath,
    phoneUploadParams,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    initializeQrCode();

    return () => {
      isMountedRef.current = false;
    };
  }, [initializeQrCode, sessionKey]);

  useEffect(() => {
    if (!downloadUrl || pollingState !== "waiting") {
      return;
    }

    const pollForUpload = async () => {
      try {
        const response = await fetch(downloadUrl);
        if (response.ok) {
          stopPolling();
          const blob = await response.blob();
          const fileType = blob.type || DEFAULT_FILE_TYPE;
          const file = new File([blob], placeholderFileName, {
            type: fileType,
          });
          if (isMountedRef.current) {
            posthog.capture("qr_code_phone_upload_received", {
              appealId,
              documentType,
              fileSizeBytes: blob.size,
            });
            onFilesUploaded([file]);
            setSessionKey((prev) => prev + 1);
          }
        }
      } catch {
        // polling continues — presigned URL expiry handles timeout naturally
      }
    };

    pollingIntervalRef.current = setInterval(
      pollForUpload,
      POLLING_INTERVAL_MS,
    );

    return () => {
      stopPolling();
    };
  }, [
    downloadUrl,
    pollingState,
    onFilesUploaded,
    stopPolling,
    placeholderFileName,
    posthog,
    appealId,
    documentType,
  ]);

  if (initError) {
    return null;
  }

  if (variant === "compact") {
    if (!qrValue) {
      return (
        <div
          className={styles.compactContainer}
          data-testid="qr-code-upload-section-compact"
        >
          <Loader size={24} />
        </div>
      );
    }

    return (
      <div
        className={`${styles.compactContainer}${disabled ? ` ${styles.containerDisabled}` : ""}`}
        data-testid="qr-code-upload-section-compact"
      >
        <QRCodeSVG value={qrValue} size={180} />
        <div className={styles.compactCaption}>
          <FontAwesomeIcon icon={faMobile} />
          <span>Scan to take photo</span>
        </div>
      </div>
    );
  }

  if (!qrValue) {
    return (
      <>
        <div className={styles.divider}>
          <div className={styles.dividerLine} />
          <span className={styles.dividerText}>OR</span>
          <div className={styles.dividerLine} />
        </div>
        <div className={styles.loadingContainer}>
          <Loader size={24} />
          <span className={styles.loadingText}>Preparing phone upload...</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.divider}>
        <div className={styles.dividerLine} />
        <span className={styles.dividerText}>OR</span>
        <div className={styles.dividerLine} />
      </div>
      <div
        className={`${styles.container}${disabled ? ` ${styles.containerDisabled}` : ""}`}
        data-testid="qr-code-upload-section"
      >
        <div className={styles.qrCode}>
          <QRCodeSVG value={qrValue} size={180} />
        </div>
        <div className={styles.textContainer}>
          <p className={styles.caption}>Scan with your phone to take a photo</p>
          <p className={styles.subCaption}>
            Open the camera app on your phone and point it at this code.
          </p>
        </div>
      </div>
    </>
  );
};
